import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import LeadDetail from './pages/LeadDetail';
import Sequences from './pages/Sequences';
import Settings from './pages/Settings';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import AcceptInvite from './pages/AcceptInvite';
import Setup from './pages/Setup';
// ── Auth Context ─────────────────────────────────────────────
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Accepts a session handed off from the marketing site's account menu
// (velaleads.ca -> app.velaleads.ca#token=...), same idea as the existing
// registration/login -> preview.html handoff, just in the other direction.
// Read synchronously (lazy useState initializer, not an effect) — this has
// to be settled before the very first render, because the "/" route just
// redirects to "/dashboard" via <Navigate>, and React Router's <Navigate>
// fires its redirect inside its OWN useEffect. Child effects run before
// parent effects in React, so that redirect (and the URL rewrite it does)
// was winning the race against an effect living here in AuthProvider,
// wiping the hash before this ever got a chance to read it — the token
// silently vanished and the user bounced to /login instead of landing
// signed in on the dashboard.
function readIncomingToken() {
  // The reset page owns its own fragment. Its link uses `reset_token` rather
  // than `token` so the two can't collide, but this path guard is the second
  // lock: nothing on /reset-password should ever be mistaken for a session
  // being handed over from the marketing site.
  if (window.location.pathname === '/reset-password') {
    return localStorage.getItem('token');
  }
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  const incomingToken = hashParams.get('token');
  if (incomingToken) {
    localStorage.setItem('token', incomingToken);
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    return incomingToken;
  }
  return localStorage.getItem('token');
}

// Set synchronously during render, not inside an effect — this is the same
// "child effects run before parent effects" hazard as readIncomingToken
// above, just hitting a different path. On any hard page load that lands
// straight on a deep authenticated route (e.g. Stripe's checkout redirect
// back to /settings, or a plain browser refresh), a child page's own
// mount-time effect can fire its first request before AuthProvider's own
// effect gets a turn to attach the Authorization header, sending it out
// unauthenticated. Found 2026-08-23 testing the real signup/checkout flow:
// /settings?billing=success 401'd its first three requests before a later
// re-render happened to retry them successfully. Mutating axios.defaults
// here is deliberately outside React's data flow (same as the DOM/
// localStorage side effect in readIncomingToken) precisely because it has
// to be settled before any child mounts, not after.
function syncAuthHeader(token) {
  if (token) axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  else delete axios.defaults.headers.common['Authorization'];
}

function AuthProvider({ children }) {
  const [token, setToken]     = useState(readIncomingToken);
  const [account, setAccount] = useState(null);
  const [accounts, setAccounts] = useState([]); // every account this login can access

  syncAuthHeader(token);

  useEffect(() => {
    if (token) {
      axios.get('/api/accounts/me')
        .then(r => setAccount(r.data))
        .catch(() => { setToken(null); localStorage.removeItem('token'); });
      axios.get('/api/auth/my-accounts')
        .then(r => setAccounts(r.data))
        .catch(() => {});
    }
  }, [token]);

  const login = (tok, acct) => {
    localStorage.setItem('token', tok);
    axios.defaults.headers.common['Authorization'] = `Bearer ${tok}`;
    setToken(tok);
    setAccount(acct);
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setAccount(null);
    setAccounts([]);
  };

  // Re-issue a token scoped to a different account this login has access to.
  const switchAccount = async (accountId) => {
    const { data } = await axios.post('/api/auth/switch-account', { accountId });
    login(data.token, data.account);
  };

  return (
    <AuthContext.Provider value={{ token, account, accounts, login, logout, switchAccount, setAccount, isLoggedIn: !!token }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Sidebar ──────────────────────────────────────────────────
function Sidebar() {
  const { account, accounts, logout, switchAccount } = useAuth();
  const navigate = useNavigate();
  const nav = [
    { to: '/dashboard',  label: 'Overview',   icon: '◈' },
    { to: '/leads',      label: 'Leads',      icon: '◉' },
    { to: '/sequences',  label: 'Sequences',  icon: '◎' },
    { to: '/settings',   label: 'Settings',   icon: '⚙' },
  ];

  return (
    <aside style={{
      width: 220, minHeight: '100vh', background: 'var(--sidebar-bg)',
      display: 'flex', flexDirection: 'column', padding: '0 0 24px',
      position: 'fixed', top: 0, left: 0, bottom: 0, zIndex: 100,
      borderRight: '1px solid var(--border)',
      backdropFilter: 'blur(20px) saturate(140%)', WebkitBackdropFilter: 'blur(20px) saturate(140%)',
    }}>
      <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }} role="img" aria-label="Vela">
          <img src="/logos/vela-sail-chrome.png" alt="" style={{ height: 24, width: 'auto', display: 'block', marginTop: -4 }} />
          <img src="/logos/vela-wordmark.png" alt="" style={{ height: 16, width: 'auto', display: 'block' }} />
        </div>
        {accounts.length > 1 ? (
          <select
            value={account?.id || ''}
            onChange={e => switchAccount(e.target.value)}
            style={{
              marginTop: 6, width: '100%', background: 'var(--card-bg)', color: 'var(--sidebar-text)',
              border: '1px solid var(--border)', borderRadius: 5, padding: '4px 6px', fontSize: 12,
            }}
          >
            {accounts.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        ) : (
          <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 2 }}>
            {account?.name || 'Loading...'}
          </div>
        )}
      </div>

      <nav style={{ flex: 1, padding: '12px 0' }}>
        {nav.map(({ to, label, icon }) => (
          <NavLink key={to} to={to} style={({ isActive }) => ({
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 20px', color: isActive ? 'var(--vela-text)' : 'var(--sidebar-text)',
            background: isActive ? 'var(--primary-light)' : 'transparent',
            borderLeft: isActive ? '3px solid var(--primary)' : '3px solid transparent',
            fontSize: 14, transition: 'all 0.15s',
          })}>
            <span style={{ fontSize: 16 }}>{icon}</span>
            {label}
          </NavLink>
        ))}
      </nav>

      <div style={{ padding: '0 20px' }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 12, marginBottom: 8 }}>
          {account?.plan?.toUpperCase()} PLAN
        </div>
        <button onClick={() => { logout(); navigate('/login'); }} style={{
          width: '100%', padding: '8px', background: 'var(--card-bg)',
          border: '1px solid var(--border)', borderRadius: 6, color: 'var(--sidebar-text)',
          cursor: 'pointer', fontSize: 13,
        }}>Sign out</button>
      </div>
    </aside>
  );
}

// ── Layout ───────────────────────────────────────────────────
function Layout({ children }) {
  return (
    <div style={{ display: 'flex' }}>
      <Sidebar />
      <main style={{ marginLeft: 220, flex: 1, padding: '32px', minHeight: '100vh' }}>
        {children}
      </main>
    </div>
  );
}

// Trialing or active — anything else (none/canceled/past_due) means no
// payment has ever gone through, so no app access at all.
function isPaid(account) {
  return !!account && ['trialing', 'active'].includes(account.subscription_status);
}

// Not a React Router route — the app and the marketing site are different
// origins, so this is a real browser navigation, not client-side routing.
function redirectToPreview(account) {
  const marketingUrl = process.env.REACT_APP_MARKETING_URL || 'https://velaleads.ca';
  const token = localStorage.getItem('token');
  const bizName = encodeURIComponent(account?.name || '');
  const plan = account?.plan || 'growth';
  const industryParam = encodeURIComponent(account?.industry || '');
  window.location.href = `${marketingUrl}/preview.html#token=${token}&plan=${plan}&biz=${bizName}&industry=${industryParam}`;
}

function ProtectedRoute({ children }) {
  const { isLoggedIn, account } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  if (account && !isPaid(account)) { redirectToPreview(account); return null; }
  if (account && !account.onboarding_completed_at) return <Navigate to="/setup" replace />;
  return <Layout>{children}</Layout>;
}

// Setup itself needs auth and payment, but must NOT bounce back into the
// onboarding check above (that would be a redirect loop) or get the
// sidebar chrome.
function SetupRoute({ children }) {
  const { isLoggedIn, account } = useAuth();
  if (!isLoggedIn) return <Navigate to="/login" replace />;
  if (account && !isPaid(account)) { redirectToPreview(account); return null; }
  return children;
}

// ── App ──────────────────────────────────────────────────────
export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login"    element={<Login />} />
          <Route path="/register" element={<Login />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invite"  element={<AcceptInvite />} />
          <Route path="/setup"    element={<SetupRoute><Setup /></SetupRoute>} />
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/leads"     element={<ProtectedRoute><Leads /></ProtectedRoute>} />
          <Route path="/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
          <Route path="/sequences" element={<ProtectedRoute><Sequences /></ProtectedRoute>} />
          <Route path="/settings"  element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
